<template>
  <div class="modern-notification-log-view pb-12">
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-paper-plane</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Bitácora de Notificaciones
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Historial de correos enviados por el sistema</div>
        </div>
      </div>
    </v-row>

    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="logs"
        :options.sync="options"
        :server-items-length="totalLogs"
        :loading="loading"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 25, 50, 100],
        }"
      >
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white flex-wrap" style="gap: 12px;">
            <v-text-field
              v-model="search"
              placeholder="Buscar por destinatario o asunto..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details
              clearable
              :disabled="loading"
              class="modern-search-field"
              style="max-width: 320px;"
              @keyup="searchTimeOut"
            />
            <v-select
              v-model="filter.status"
              :items="statusOptions"
              label="Estado"
              outlined dense hide-details clearable
              style="max-width: 200px;"
              class="modern-select"
            />
            <v-select
              v-model="filter.notification_type"
              :items="typeOptions"
              label="Tipo"
              outlined dense hide-details clearable
              style="max-width: 260px;"
              class="modern-select"
            />
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small :color="statusColor(item.status)" dark label class="font-weight-black">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.notification_type="{ item }">
          <span class="body-2 font-weight-medium">{{ typeLabel(item.notification_type) }}</span>
        </template>

        <template v-slot:item.recipients="{ item }">
          <div class="body-2 text-truncate" style="max-width: 260px;">{{ item.recipients }}</div>
        </template>

        <template v-slot:item.sent_at="{ item }">
          <span v-if="item.sent_at" class="body-2">{{ item.sent_at | moment("DD/MM/YYYY HH:mm") }}</span>
          <span v-else class="body-2 grey--text">—</span>
        </template>

        <template v-slot:item.error_message="{ item }">
          <v-tooltip bottom max-width="400" v-if="item.error_message">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="body-2 error--text text-truncate d-inline-block" style="max-width: 220px;">
                {{ item.error_message }}
              </span>
            </template>
            <span>{{ item.error_message }}</span>
          </v-tooltip>
          <span v-else class="body-2 grey--text">—</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.status === 'failed'"
            small
            color="primary"
            text
            :loading="retryingId === item.id"
            class="font-weight-bold text-none"
            @click="retry(item)"
          >
            <v-icon left small>fas fa-redo</v-icon>
            Reintentar
            <span v-if="item.retry_count" class="ml-1 caption grey--text">({{ item.retry_count }})</span>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { fetchNotificationLogs, retryNotification } from "@/api/notifications";
import notifications from "@/utils/notifications";

export default {
  name: "NotificationLog",
  data: () => ({
    loading: false,
    retryingId: null,
    logs: [],
    totalLogs: 0,
    search: "",
    timer: null,
    options: {
      sortBy: ["created_at"],
      sortDesc: [true],
    },
    filter: {
      status: "",
      notification_type: "",
    },
    statusOptions: [
      { text: "Enviado", value: "sent" },
      { text: "Fallido", value: "failed" },
      { text: "Pendiente", value: "pending" },
    ],
    typeOptions: [
      { text: "Recordatorio Vendedor", value: "invoicing_reminder_seller" },
      { text: "Resumen Gerente", value: "invoicing_reminder_manager" },
    ],
  }),
  computed: {
    headers() {
      return [
        { text: "Fecha", align: "start", value: "created_at", class: "table-header-custom", width: "160" },
        { text: "Tipo", align: "start", value: "notification_type", class: "table-header-custom" },
        { text: "Destinatarios", align: "start", value: "recipients", class: "table-header-custom" },
        { text: "Asunto", align: "start", value: "subject", class: "table-header-custom" },
        { text: "Estado", align: "center", value: "status", class: "table-header-custom", width: "120" },
        { text: "Enviado", align: "start", value: "sent_at", class: "table-header-custom", width: "160" },
        { text: "Error", align: "start", value: "error_message", class: "table-header-custom", sortable: false },
        { text: "", align: "end", value: "actions", sortable: false, class: "table-header-custom", width: "160" },
      ];
    },
  },
  methods: {
    statusColor(status) {
      if (status === "sent") return "success";
      if (status === "failed") return "error";
      return "grey";
    },
    statusLabel(status) {
      const found = this.statusOptions.find((o) => o.value === status);
      return found ? found.text : status;
    },
    typeLabel(type) {
      const found = this.typeOptions.find((o) => o.value === type);
      return found ? found.text : type;
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.fetch(); }, 800);
    },
    async fetch() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const params = new URLSearchParams();
      params.append("page", page || 1);
      params.append("page_size", itemsPerPage || 25);
      if (this.search) params.append("search", this.search);
      if (this.filter.status) params.append("status", this.filter.status);
      if (this.filter.notification_type) params.append("notification_type", this.filter.notification_type);
      if (sortBy && sortBy[0]) {
        params.append("ordering", sortDesc[0] ? `-${sortBy[0]}` : sortBy[0]);
      }
      try {
        const data = await fetchNotificationLogs(params.toString());
        this.logs = data.results;
        this.totalLogs = data.count;
      } catch (e) {
        notifications.showError("Error al cargar la bitácora de notificaciones");
      } finally {
        this.loading = false;
      }
    },
    async retry(item) {
      this.retryingId = item.id;
      try {
        await retryNotification(item.id);
        notifications.showSuccess("Reintento enviado");
        this.fetch();
      } catch (e) {
        notifications.showError("Error al reintentar el envío");
      } finally {
        this.retryingId = null;
      }
    },
  },
  watch: {
    filter: {
      deep: true,
      handler: "fetch",
    },
    options: {
      handler: "fetch",
      deep: true,
    },
  },
};
</script>
